"use client";

import { useState, useEffect, useMemo } from "react";
import { Search, SlidersHorizontal, Plus } from "lucide-react";
import { ServicesPanel } from "../../components/categories/ServicesPanel";
import { ServiceModal } from "../../components/categories/ServiceModal";
import { BundleModal } from "../../components/categories/BundleModal";
import { DeleteConfirmModal } from "../../components/categories/DeleteConfirmModal";
import {
  FiltersModal,
  ServiceFilters,
} from "../../components/categories/FiltersModal";
import { BusinessService, ServiceStaffMember } from "../../types/businessService";
import { Category } from "../../types/categories";
import { Employee } from "../../types/employee";
import {
  getBusinessServices,
  createBusinessService,
  updateBusinessService,
  deleteBusinessService,
  CreateBusinessServicePayload,
  getCreateFormData,
  getEditFormData,
  mapApiServiceToBusinessService,
  storeServiceImages,
  updateServiceImages,
  Tax,
  ServiceImage,
} from "../../services/businessServicesApi";
import { getCategories } from "../../services/CategoriesApi";
import { getEmployees } from "../../services/employeesApi";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../hooks/useToast";
import PageHeader from "../../components/ui/PageHeader";
import { Briefcase } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

export default function ServicesPage() {
  const [searchTerm, setSearchTerm] = useState("");

  const { user } = useAuth();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // --- Data ---
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);

  const [services, setServices] = useState<BusinessService[]>([]);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [servicesError, setServicesError] = useState<string | null>(null);

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [employeesError, setEmployeesError] = useState<string | null>(null);

  const [taxes, setTaxes] = useState<Tax[]>([]);
  const [taxesLoading, setTaxesLoading] = useState(true);
  const [taxesError, setTaxesError] = useState<string | null>(null);

  const [editImages, setEditImages] = useState<ServiceImage[]>([]);

  const [savingService, setSavingService] = useState(false);
  const [deletingService, setDeletingService] = useState(false);
  const toast = useToast();

  // Helper to get location_id from user context
  const getLocationId = (): number => {
    const toNumber = (
      ...values: Array<string | number | undefined | null>
    ): number | undefined => {
      for (const value of values) {
        if (typeof value === "number" && Number.isFinite(value)) return value;
        if (typeof value === "string" && value.trim() !== "") {
          const parsed = Number(value);
          if (!Number.isNaN(parsed)) return parsed;
        }
      }
      return undefined;
    };
    return (
      toNumber(
        user?.location_id,
        user?.location?.id,
        user?.locationId,
        user?.default_location_id
      ) ?? 1
    );
  };

  // --- Filters ---
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [filters, setFilters] = useState<ServiceFilters>({
    status: "all",
    serviceType: "all",
    employeeId: "all",
    categoryId: "all",
  });

  const employeeOptions = useMemo(() => {
    const map = new Map<string, string>();
    employees.forEach((employee) =>
      map.set(String(employee.id), employee.name)
    );
    services.forEach((service) => {
      service.staff.forEach((member) => {
        if (member.id && member.name) {
          map.set(member.id, member.name);
        }
      });
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [employees, services]);

  const serviceTypeOptions = useMemo(() => {
    const set = new Set<string>();
    services.forEach((service) => {
      if (service.serviceType) {
        set.add(service.serviceType);
      }
    });
    return Array.from(set);
  }, [services]);

  // --- Combine search + filters + pagination ---
  const filteredServices = useMemo(() => {
    return services
      .filter((s) =>
        filters.categoryId === "all" ? true : s.categoryId === filters.categoryId
      )
      .filter((s) =>
        s.name.toLowerCase().includes(searchTerm.toLowerCase().trim())
      )
      .filter((s) =>
        filters.status === "all" ? true : s.status === filters.status
      )
      .filter((s) =>
        filters.serviceType === "all"
          ? true
          : s.serviceType === filters.serviceType
      )
      .filter((s) =>
        filters.employeeId === "all"
          ? true
          : s.staff.some((member) => member.id === filters.employeeId)
      );
  }, [services, searchTerm, filters]);

  // Paginated services
  const paginatedServices = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredServices.slice(startIndex, endIndex);
  }, [filteredServices, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredServices.length / itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  // --- Modal state ---
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [serviceMode, setServiceMode] = useState<"add" | "edit">("add");
  const [editingService, setEditingService] = useState<BusinessService | null>(
    null
  );

  const [showBundleModal, setShowBundleModal] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    type: "service";
    id: string;
    name: string;
  } | null>(null);

  // --- Fetch categories & services ---
  const fetchCategories = async () => {
    setCategoriesLoading(true);
    setCategoriesError(null);
    try {
      const data = await getCategories();
      // Ensure categories is always an array and log for debugging
      const categoriesArray = Array.isArray(data) ? data : [];
      console.log("Fetched categories:", categoriesArray);
      setCategories(categoriesArray);
    } catch (error) {
      console.error("Failed to fetch categories", error);
      setCategoriesError("Failed to load categories.");
      setCategories([]); // Ensure categories is always an array even on error
    } finally {
      setCategoriesLoading(false);
    }
  };

  const fetchServices = async () => {
    setServicesLoading(true);
    setServicesError(null);
    try {
      const data = await getBusinessServices();
      setServices(data);
    } catch (error) {
      console.error("Failed to fetch services", error);
      setServicesError("Failed to load services.");
    } finally {
      setServicesLoading(false);
    }
  };

  const fetchEmployees = async () => {
    setEmployeesLoading(true);
    setEmployeesError(null);
    try {
      const data = await getEmployees();
      setEmployees(data);
    } catch (error) {
      console.error("Failed to fetch employees", error);
      setEmployeesError("Failed to load employees.");
    } finally {
      setEmployeesLoading(false);
    }
  };

  const fetchTaxes = async () => {
    setTaxesLoading(true);
    setTaxesError(null);
    try {
      const formData = await getCreateFormData();
      setTaxes(formData.taxes || []);
    } catch (error) {
      console.error("Failed to fetch taxes", error);
      setTaxesError("Failed to load taxes.");
      setTaxes([]);
    } finally {
      setTaxesLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchServices();
    fetchEmployees();
    fetchTaxes(); // Pre-fetch taxes on page load to avoid delay
  }, []);



  const defaultCategoryId =
    filters.categoryId !== "all"
      ? filters.categoryId
      : categories[0]?.id ?? "";

  // --- Service Handlers ---
  const handleServiceAdd = () => {
    setServiceMode("add");
    setEditingService(null);
    setEditImages([]);
    setShowServiceModal(true); // Open modal immediately
    fetchTaxes(); // Fetch in background if not already pre-fetched
  };

  const handleServiceEdit = async (service: BusinessService) => {
    setServiceMode("edit");
    setShowServiceModal(true);

    // Fetch edit form data (including taxes and selected values) when opening edit modal
    try {
      setTaxesLoading(true);
      const editData = await getEditFormData(service.id);
      console.log("Edit form data received:", editData);
      console.log("selectedTax from API:", editData.selectedTax);
      console.log("service.service_taxes from API:", editData.service?.service_taxes);

      setTaxes(editData.taxes || []);

      // Update editingService with fresh data from API, including selectedTax
      const updatedService = mapApiServiceToBusinessService(editData.service);

      // Map selectedTax IDs to the service taxes array
      // Handle both null and array cases - selectedTax should be an array of tax IDs
      if (editData.selectedTax !== null && editData.selectedTax !== undefined && Array.isArray(editData.selectedTax) && editData.selectedTax.length > 0) {
        const taxIds = editData.selectedTax.map((item: any) => {
          // Handle case where item is an ID directly
          if (typeof item === 'number' || typeof item === 'string') {
            const id = typeof item === 'string' ? parseInt(item) : item;
            return id;
          }
          // Handle case where item is an object with tax_id (common in current API)
          if (item && typeof item === 'object') {
            const taxId = item.tax_id || item.id;
            return typeof taxId === 'string' ? parseInt(taxId) : taxId;
          }
          return null;
        }).filter((id): id is number => id !== null && !isNaN(id) && id > 0);

        (updatedService as any).taxes = taxIds;
        console.log("✅ Setting taxes from selectedTax:", taxIds);
      } else {
        // Also check service_taxes relationship if selectedTax is null/empty
        const serviceTaxes = editData.service?.service_taxes;
        if (serviceTaxes && Array.isArray(serviceTaxes) && serviceTaxes.length > 0) {
          const taxIds = serviceTaxes.map((st: any) => {
            const taxId = st.tax_id || st.tax?.id || st.id;
            return typeof taxId === 'string' ? parseInt(taxId) : taxId;
          }).filter(id => !isNaN(id) && id > 0);
          (updatedService as any).taxes = taxIds;
          console.log("✅ Setting taxes from service_taxes relationship:", taxIds, "from service.service_taxes:", serviceTaxes);
        } else {
          (updatedService as any).taxes = [];
          console.warn("⚠️ No taxes found - selectedTax:", editData.selectedTax, "service_taxes:", serviceTaxes, "- This service may not have taxes saved");
        }
      }

      // Map selectedUsers to staff
      if (editData.selectedUsers && Array.isArray(editData.selectedUsers)) {
        const selectedEmployees = editData.employees.filter(emp =>
          editData.selectedUsers.includes(emp.id)
        );
        updatedService.staff = selectedEmployees.map(emp => ({
          id: String(emp.id),
          name: emp.name
        }));
      }

      // Set the updated service after fetching all data
      setEditingService(updatedService);

      // Store images data for the modal
      setEditImages(editData.images || []);
    } catch (error) {
      console.error("Failed to fetch edit form data", error);
      setTaxesError("Failed to load edit form data.");
      // Fallback to original service data
      setEditingService(service);
    } finally {
      setTaxesLoading(false);
    }
  };

  const handleServiceDelete = (id: string, name: string) => {
    setDeleteTarget({ type: "service", id, name });
    setShowDeleteModal(true);
  };

  // --- Bundle Handlers ---
  const handleBundleAdd = () => {
    setShowBundleModal(true);
  };

  return (
    <div className="p-4 lg:p-6">
      <div className="flex flex-col w-full h-full">

        {/* Header */}
        <PageHeader
          title="Services"
          description="Manage your business services and offerings"
          icon={Briefcase}
          variant="card"
          className="mb-0"
          mobileActionsInHeader={true}
          actions={
            <button
              onClick={handleServiceAdd}
              className="px-3 py-1.5 lg:px-6 lg:py-3 bg-[#885ABB] hover:bg-[#6f47a2] text-white rounded-lg text-[10px] lg:text-base font-bold lg:font-medium transition-all transform hover:-translate-y-0.5 flex items-center gap-1.5 lg:gap-2 shadow-sm"
            >
              <Plus className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
              <span>Add Service</span>
            </button>
          }
        />

        {/* Toolbar */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between py-4 gap-4">
          {/* Left: Per Page Selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 font-medium whitespace-nowrap">Show</span>
            <Select
              value={itemsPerPage.toString()}
              onValueChange={(value) => {
                setItemsPerPage(Number(value));
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="w-[80px] bg-white border-gray-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-gray-500 font-medium whitespace-nowrap">per page</span>
          </div>

          {/* Right: Filters and Search */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            {/* Filters Button */}
            <button
              onClick={() => setShowFiltersModal(true)}
              className="flex items-center justify-center gap-2 bg-white border border-gray-200 rounded-lg px-4 py-2 text-[14px] font-medium text-gray-800 hover:bg-purple-50 hover:border-[#885ABB] transition-colors"
            >
              <SlidersHorizontal className="w-4 h-4 text-[#885ABB]" strokeWidth={3} />
              <span>Filters</span>
            </button>

            {/* Search */}
            <div className="relative w-full lg:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search services..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2 text-[14px] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-transparent transition-all"
              />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 pb-6">
          <ServicesPanel
            services={paginatedServices}
            categoryName={
              filters.categoryId !== "all"
                ? categories.find((c) => c.id === filters.categoryId)?.name ||
                "Services"
                : "All Services"
            }
            onServiceAdd={handleServiceAdd}
            onServiceEdit={handleServiceEdit}
            onServiceDelete={handleServiceDelete}
            isLoading={servicesLoading}
            error={servicesError}
            onRetry={fetchServices}
            taxes={taxes}
          />

          {/* Pagination */}
          {!servicesLoading && !servicesError && totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 mt-4 border-t border-gray-200 bg-white rounded-b-xl">
              <div className="text-sm text-gray-600 text-center sm:text-left">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredServices.length)} of {filteredServices.length} services
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600 whitespace-nowrap">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Modals */}
        {showFiltersModal && (
          <FiltersModal
            isOpen={showFiltersModal}
            onClose={() => setShowFiltersModal(false)}
            onApply={(newFilters) => setFilters(newFilters)}
            teamMembers={employeeOptions}
            serviceTypes={serviceTypeOptions}
            initialFilters={filters}
            categories={categories}
          />
        )}

        {showServiceModal && (
          <ServiceModal
            isOpen={showServiceModal}
            mode={serviceMode}
            service={editingService}
            onClose={() => setShowServiceModal(false)}
            saving={savingService}
            taxes={taxes}
            taxesLoading={taxesLoading}
            existingImages={editImages}
            onSave={async (data, imageData) => {
              if (serviceMode === "add") {
                setSavingService(true);
                try {
                  const targetCategoryId = data.categoryId || defaultCategoryId;
                  const categoryIdNum = parseInt(targetCategoryId);
                  if (Number.isNaN(categoryIdNum)) {
                    throw new Error("Invalid category selected");
                  }

                  // Convert employee IDs from string to number
                  const employeeIds = data.staff
                    .map((member) => {
                      const id = typeof member.id === "string" ? parseInt(member.id) : member.id;
                      return Number.isNaN(id) ? null : id;
                    })
                    .filter((id): id is number => id !== null);

                  // Tax IDs are already numbers from the form
                  const taxIds = data.taxes.filter((id): id is number => typeof id === 'number' && !isNaN(id));

                  // Convert duration back to original unit if needed
                  // ServiceModal converts to minutes, so we need to convert back if timeUnit is hours
                  const timeValue = data.timeUnit === "hours"
                    ? Math.round(data.duration / 60)
                    : data.duration;

                  const discountNum = Math.max(0, Number(data.discount) || 0);

                  const payload: CreateBusinessServicePayload = {
                    name: data.name.trim(),
                    slug: data.slug.trim(),
                    description: data.description || undefined,
                    price: data.price,
                    time: timeValue,
                    time_type: data.timeUnit,
                    category_id: categoryIdNum,
                    location_id: getLocationId(),
                    service_type: "offline",
                    status: data.status,
                    discount: discountNum,
                    discount_type: data.discountType,
                    employee_ids: employeeIds.length > 0 ? employeeIds : undefined,
                    tax_ids: taxIds.length > 0 ? taxIds : undefined,
                    enable_deposite: data.enableDeposit ? "yes" : "no",
                    website_visibility: data.websiteVisibility ? "show" : "hide",
                    ...(data.enableDeposit && {
                      deposite_type: data.depositType,
                      deposte_amount: data.depositAmount,
                    }),
                  };

                  const createdService = await createBusinessService(payload);

                  // Upload images if provided
                  if (imageData && imageData.newFiles.length > 0) {
                    try {
                      await storeServiceImages(
                        createdService.id,
                        imageData.newFiles,
                        imageData.defaultImage
                      );
                    } catch (imageError: any) {
                      console.error("Failed to upload images:", imageError);
                      toast.error("Service created but images failed to upload");
                    }
                  }

                  // Refresh services list
                  await fetchServices();
                  setShowServiceModal(false);
                  toast.success("Service created successfully!");
                } catch (error: any) {
                  console.error("Failed to create service", error);
                  toast.error(error.response?.data?.message || "Failed to create service. Please try again.");
                } finally {
                  setSavingService(false);
                }
              } else if (serviceMode === "edit") {
                if (!editingService) {
                  console.error("Editing service but editingService is null");
                  alert("Error: No service selected for editing.");
                  return;
                }
                setSavingService(true);
                try {
                  const targetCategoryId = data.categoryId || editingService.categoryId;
                  const categoryIdNum = parseInt(targetCategoryId);
                  if (Number.isNaN(categoryIdNum)) {
                    throw new Error("Invalid category selected");
                  }

                  // Convert employee IDs from string to number
                  const employeeIds = data.staff
                    .map((member) => {
                      const id = typeof member.id === "string" ? parseInt(member.id) : member.id;
                      return Number.isNaN(id) ? null : id;
                    })
                    .filter((id): id is number => id !== null);

                  // Tax IDs are already numbers from the form
                  const taxIds = data.taxes.filter((id): id is number => typeof id === 'number' && !isNaN(id));

                  // Convert duration back to original unit if needed
                  // ServiceModal converts to minutes, so we need to convert back if timeUnit is hours
                  const timeValue = data.timeUnit === "hours"
                    ? Math.round(data.duration / 60)
                    : data.duration;

                  const discountNum = Math.max(0, Number(data.discount) || 0);

                  const payload: CreateBusinessServicePayload = {
                    name: data.name.trim(),
                    slug: data.slug.trim(),
                    description: data.description || undefined,
                    price: data.price,
                    time: timeValue,
                    time_type: data.timeUnit,
                    category_id: categoryIdNum,
                    location_id: getLocationId(),
                    service_type: editingService.serviceType || "offline",
                    status: data.status,
                    discount: discountNum,
                    discount_type: data.discountType,
                    employee_ids: employeeIds.length > 0 ? employeeIds : undefined,
                    tax_ids: taxIds.length > 0 ? taxIds : undefined,
                    enable_deposite: data.enableDeposit ? "yes" : "no",
                    website_visibility: data.websiteVisibility ? "show" : "hide",
                    ...(data.enableDeposit && {
                      deposite_type: data.depositType,
                      deposte_amount: data.depositAmount,
                    }),
                  };

                  await updateBusinessService(editingService.id, payload);

                  // Update images if provided
                  if (imageData) {
                    try {
                      await updateServiceImages(
                        editingService.id,
                        imageData.newFiles,
                        imageData.existingImages,
                        imageData.defaultImage
                      );
                    } catch (imageError: any) {
                      console.error("Failed to update images:", imageError);
                      toast.error("Service updated but images failed to update");
                    }
                  }

                  // Refresh services list
                  await fetchServices();
                  setShowServiceModal(false);
                  toast.success("Service updated successfully!");
                } catch (error: any) {
                  console.error("Failed to update service", error);
                  toast.error(error.response?.data?.message || "Failed to update service. Please try again.");
                } finally {
                  setSavingService(false);
                }
              } else {
                console.error("Unknown service mode", { serviceMode, editingService });
                alert("Error: Unknown service mode. Please try again.");
              }
            }}
            categories={categories}
            activeCategory={defaultCategoryId}
            employees={employees}
            employeesLoading={employeesLoading}
            categoriesLoading={categoriesLoading}
          />
        )}

        {showBundleModal && (
          <BundleModal
            isOpen={showBundleModal}
            onClose={() => setShowBundleModal(false)}
            onSave={(data) => {
              setServices([
                ...services,
                {
                  ...data,
                  id: Date.now().toString(),
                  categoryId: data.categoryId,
                  categoryName:
                    categories.find((c) => c.id === data.categoryId)?.name ||
                    "Uncategorized",
                  price: data.retailPrice || 0,
                  status: "active",
                  serviceType: "bundle",
                  discountPrice: undefined,
                  discountType: undefined,
                  duration: 0,
                  durationLabel: "Bundle",
                  staff: [],
                  staffNames: [],
                  imageUrl: null,
                },
              ]);
              setShowBundleModal(false);
            }}
            categories={categories}
            services={services}
          />
        )}

        {showDeleteModal && deleteTarget && (
          <DeleteConfirmModal
            isOpen={showDeleteModal}
            type={deleteTarget.type}
            name={deleteTarget.name}
            onClose={() => setShowDeleteModal(false)}
            isLoading={deletingService}
            onConfirm={async () => {
              if (deleteTarget.type === "service") {
                setDeletingService(true);
                try {
                  await deleteBusinessService(deleteTarget.id);
                  // Refresh services list
                  await fetchServices();
                  setShowDeleteModal(false);
                  toast.success("Service deleted successfully!");
                } catch (error: any) {
                  console.error("Failed to delete service", error);
                  toast.error(error.response?.data?.message || "Failed to delete service. Please try again.");
                } finally {
                  setDeletingService(false);
                }
              } else {
                setShowDeleteModal(false);
              }
            }}
          />
        )}
      </div>
    </div>
  );
}
